import get from 'lodash/get'
import posthog from 'posthog-js'
import { UserType } from 'src/context/AuthContext.type'
import filterGraphManager from 'src/context/NewFilterContext/utils/FilterGraphManager'
import { getAllAccessLevels } from 'src/services/firebase-compat'
import { updateDocument, verifyUser } from 'src/services/firebase/helper'
import { metricsService } from 'src/services/metrices'
import { AdminService, DefaultService } from 'src/services/openApiV2'
import { sendSlackNotification } from 'src/utils/api'
import { APP_VERSION } from 'src/utils/config'
import { accessLevelConfig, AccessLevelEnum, Modules } from 'src/utils/config/config'
import { Dictionary } from 'src/utils/config/dictionary'
import { getAccessLevel } from 'src/utils/functions/accessLevel'
import { getFrontendAlertChannel } from 'src/utils/functions/slackSupport'

interface InviteTeammateValues {
  name: string
  email: string
  access_level: string
  subchains: string | string[]
  locations: any[]
  send_invitation?: boolean
  hide_explore_section?: boolean
}

export const accessLevelStepOptions = [
  {
    label: accessLevelConfig[AccessLevelEnum.BUSINESS_ADMIN].label,
    value: AccessLevelEnum.BUSINESS_ADMIN,
    description: 'Enables invitation and admin rights assignment for team members, with access to all permitted modules.'
  },
  {
    label: accessLevelConfig[AccessLevelEnum.FINANCE_TEAM].label,
    value: AccessLevelEnum.FINANCE_TEAM,
    description: `Limits users to ${Modules.Balance} modules; does not permit inviting new members.        `
  },
  {
    label: accessLevelConfig[AccessLevelEnum.STORE_MANAGER].label,
    value: AccessLevelEnum.STORE_MANAGER,
    description: 'Grants access to Store Manager modules only; does not permit inviting new members.        '
  },
  {
    label: accessLevelConfig[AccessLevelEnum.INTERNAL_OPERATOR].label,
    value: AccessLevelEnum.INTERNAL_OPERATOR,
    description:
      'Allows inviting Store Managers and Observers within a franchisee group, and assigning them to specific stores; with access to all permitted modules.'
  },
  {
    label: accessLevelConfig[AccessLevelEnum.BUSINESS_MANAGER].label,
    value: AccessLevelEnum.BUSINESS_MANAGER,
    description: `User can have multiple ${Dictionary.FRANCHISEE.toLowerCase()} access. Allows inviting Store Managers and Observers within a franchisee group, and assigning them to specific stores; with access to all permitted modules.`
  },
  {
    label: accessLevelConfig[AccessLevelEnum.OBSERVER].label,
    value: AccessLevelEnum.OBSERVER,
    description: 'Receives store performance reports and alerts without login access; cannot invite new members.'
  },
  {
    label: accessLevelConfig[AccessLevelEnum.OWNER_VIEW].label,
    value: AccessLevelEnum.OWNER_VIEW,
    description: 'Access to all modules and features; cannot invite new members.'
  }
]

export const franchiseGroupStepFunction = async (currentUser: UserType) => {
  try {
    const res = await DefaultService.getSubchainsV2SubchainGetAllV2Get({ chain: get(currentUser, 'org', '') })
    return res.map((item) => ({
      label: item.name,
      value: item.id
    }))
  } catch (error) {
    console.error('Error in getting franchise groups', error)
    throw error
  }
}

export const locationStepFunction = async (currentUser: UserType) => {
  try {
    const mainGraph = filterGraphManager.getGraph('main')
    const bNames = mainGraph.getAllPossibleNodesByKey('b_name')
    const locs = bNames.map((item) => {
      const bNameIds = Array.from(get(item, 'relations.b_name_id', new Map())?.keys())
      return {
        label: item.value,
        value: bNameIds[0]
      }
    })
    return locs
  } catch (error) {
    console.error('Error in getting locations', error)
    throw error
  }
}

export const inviteTeammate = async (currentUser: UserType, values: InviteTeammateValues) => {
  try {
    const subchainIds = (Array.isArray(values.subchains) ? values.subchains : [values.subchains]).map((item) => Number(item))

    const subchains = await franchiseGroupStepFunction(currentUser)
    const subchainNames = subchains.filter((item) => subchainIds.includes(item.value)).map((item) => item.label)

    const body = {
      ...values,
      subchains: subchainNames,
      send_invitation: values.access_level === AccessLevelEnum.OBSERVER ? false : true,
      hide_explore_section: values.access_level !== AccessLevelEnum.BUSINESS_ADMIN
    }
    const result = (await DefaultService.inviteOrgUserOrgInviteUserPost({ requestBody: body })) as any

    if (
      getAllAccessLevels(values.access_level)?.is_custom ||
      values.access_level === AccessLevelEnum.STORE_MANAGER ||
      values.access_level === AccessLevelEnum.OWNER_VIEW ||
      values.access_level === AccessLevelEnum.OBSERVER
    ) {
      if (values.locations?.length > 0) {
        await AdminService.updateBnamesToUserAdminUpdateBnamesPut({
          requestBody: {
            bname_ids: values.locations,
            user_id: get(result, 'user_id')
          }
        })
      }
    }

    posthog.capture('User Invited through wizard actions', {
      inviterEmail: currentUser.email,
      inviterName: currentUser.name,
      inviteeEmail: values.email,
      inviteeName: values.name,
      org: currentUser.org
    })

    if (subchains) {
      try {
        const res = await DefaultService.mapUsersToSubchainsSubchainMapUserPost({
          chain: get(currentUser, 'org', ''),
          requestBody: {
            user_id_list: [result.user_id],
            sub_chains: subchainNames
          }
        })
      } catch (err) {
        console.error('Error in mapping users to subchains', err)
        throw err
      }
    }

    await updateDocument('users', result.user_id, {
      'access-level': values.access_level,
      config: {
        is_report_only: values.access_level === AccessLevelEnum.OBSERVER
      }
    })
    sendSlackNotification({
      message: `${currentUser.email} invited  ${values.email} from ${currentUser.org} Org`,
      title: `User Invited from User Management actions wizard : Success`,
      channel: 'fe-logs'
    })
    verifyUser({ email: values.email }) // Adding temporary [need to used the serviceClient variable]

    return result?.message || 'Invitiation sent successfully'
  } catch (error) {
    metricsService.trackError(
      'GENERAL',
      'not_applicable',
      'unknown',
      'User Invited from User Management through wizardactions : Failed',
      window.location.pathname,
      {
        userType: process.env.NODE_ENV === 'production' ? 'external' : 'internal',
        duration: -1,
        version: APP_VERSION,
        pageUrl: window.location.pathname,
        apiUrl: 'not applicable',
        email: currentUser.email,
        org: currentUser.org,
        accessLevel: getAccessLevel(currentUser),
        methodName: 'not applicable'
      }
    )
    await sendSlackNotification({
      message: `${currentUser.email} invited  ${values.email} from ${currentUser.org} Org, ${error?.body?.message || error.message}`,
      channel: getFrontendAlertChannel(),
      title: 'User Invited from User Management actions wizard : Failed'
    })

    console.error('Error in inviting teammate', error)

    throw error?.body?.message || error.message || 'Invitiation failed'
  }
}
